import React, { useEffect, useState } from 'react';
import { AdjudicationNoteCategoryInternal, CommentType } from '@hobt/claim-domain';
import { Text } from '@sitecore-jss/sitecore-jss-react';
import { AttachmentsView } from 'Components/Common/AttachmentsView';
import { Button } from 'Components/Common/Button';
import FormDropdown from 'Components/Inputs/FormDropdown';
import FormTextArea from 'Components/Inputs/FormTextArea';
import { useCorrespondenceForm } from 'Feature/Claims/components/Details/AttachmentsNotesCommentsDetails/useCorrespondenceForm';
import { useHBTFormContext } from 'Feature/Claims/components/HBTFormContext';
import i18n from 'i18next';
import { Scrollbars } from 'react-custom-scrollbars-2';
import { FormProvider } from 'react-hook-form';
import { config } from '../../../../../../config';
import styles from './styles.module.scss';
import { AddInternalNoteFormProps, AllowedFileTypes } from './types';

const SECTION_ID = 'ClaimsDetailsAddNote';

export const AddInternalNoteForm: React.FC<AddInternalNoteFormProps> = ({
  fields,
  onSuccessCallback,
  onErrorCallback,
  onCancelCallback
}: AddInternalNoteFormProps) => {
  const { uuid: claimId } = useHBTFormContext();

  const { hookForm, onSubmit, onCancel, disableSubmitBtn } = useCorrespondenceForm(
    onSuccessCallback,
    onErrorCallback,
    onCancelCallback,
    claimId
  );
  const watchCategory = hookForm.watch('adjudicationNoteCategoryCode');
  const [otherCategory, setotherCategory] = useState(false);

  const categoryFields = fields.categoryList?.fields?.listItems?.filter?.(
    (item: FieldListItem) =>
      item.fields.itemName.value !== 'Claim Status Override' &&
      item.fields.itemName.value !== 'Outrepasser le statut de la demande'
  );
  useEffect(() => hookForm.setValue('commentTypeCode', CommentType.InternalNote), []);

  useEffect(() => {
    watchCategory == 5 ? setotherCategory(true) : setotherCategory(false);
  }, [watchCategory]);

  return (
    <Scrollbars>
      <div className={styles.headerWrapper}>
        <Text tag="h2" field={fields.formTitle} />
        <div className={styles.closeButton}>
          <button
            type="button"
            data-testid={`${SECTION_ID}FormCloseButton`}
            onClick={onCancel}
            aria-label={i18n.t('Accessibility-Close-Button') ?? ''}
          >
            <span className="material-icons align-self-center">close</span>
          </button>
        </div>
      </div>

      <FormProvider {...hookForm}>
        <form
          id={`${SECTION_ID}Form`}
          className={styles.documentRequestWrapper}
          onSubmit={hookForm.handleSubmit(onSubmit)}
          noValidate
        >
          <p className={styles.textWrapper}>
            <Text field={fields.content} />
          </p>

          <div className={styles.categoriesWrapper}>
            <FormDropdown
              className=""
              name="adjudicationNoteCategoryCode"
              label={fields.categoryLabel}
              options={categoryFields}
            />
          </div>

          {otherCategory && (
            <div className={styles.templateWrapper}>
              <FormTextArea
                name="otherAdjudicationNoteCategoryDescription"
                className={styles.templateTextArea}
                label={fields.otherDescription}
                textAreaHelperText={fields.characterLimit.value}
                charLimit={parseInt(fields.limitNumber.value ?? '0', 10)}
              />
            </div>
          )}

          <div className={styles.categoriesWrapper}>
            <FormDropdown
              className=""
              name="propertyDeficiencyCategoryCode"
              label={fields.propertyDeficiencyLabel}
              options={fields.propertyDeficiencyList?.fields?.listItems}
              optional={true}
              disableDefaultSelect={false}
            />
          </div>

          <div className={styles.templateWrapper}>
            <FormTextArea
              name="remarkText"
              className={styles.templateTextArea}
              label={fields.description}
              textAreaHelperText={fields.characterLimit.value}
              charLimit={parseInt(fields.limitNumber.value ?? '0', 10)}
            />
          </div>

          {React.createElement(AttachmentsView, {
            isOptional: true,
            registeredVal: 'attachments',
            fields: fields.attachmentsView.fields,
            endpoint: config.claimApi.urls.upload,
            allowedFileTypes: AllowedFileTypes
          })}

          <div className={styles.buttonWrapper}>
            <Button
              loading
              responsive
              type="submit"
              name={'submitButton'}
              text={fields.submitBtn}
              ariaText={fields.submitBtn}
              disabled={disableSubmitBtn}
            />
            <Button
              responsive
              type="button"
              secondaryButton
              name={`cancelButton`}
              onClick={onCancel}
              text={fields.cancelBtn}
              ariaText={fields.cancelBtn}
            />
          </div>
        </form>
      </FormProvider>
    </Scrollbars>
  );
};
